import React, { useEffect, useState } from "react";
import { DataTableCol } from "./types";
import { useNavigate } from "react-router-dom";
import { IoCheckmark } from "react-icons/io5";
import Pagination from "@mui/material/Pagination";
import moment from "moment";

interface Props {
  tableData: any;
  TableCols: DataTableCol[];
  editInfo?: any;
  deleteInfo?: any;
  viewInfo?: any;
  onActions?: (data: any) => void;
  onPageChange?: (data: any) => void;
  onRowClickEvent?: (data: any) => void;
  pageCount: any;
  onSelectPageChange?: (data: any) => void;
  onSorting?: (data: any) => void;
  pagination?: boolean;
  canShowCheckbox?: boolean;
  selectCheckBox?: (data: any) => void;
}

export const DataTable: React.FC<Props> = (props: Props) => {
  const [sortingField, setSortingField] = useState(props?.pageCount?.sorting_field);
  const [sortingOrder, setSortingOrder] = useState(props?.pageCount?.sorting_type);
  const [itemOffset, setItemOffset] = useState<any>(0);
  const [canShowDefaultArrow, setCanShowDefaultArrow] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState<any>(10);
  const items: any[] = props?.tableData?.results || [];

  useEffect(() => {
    if (props?.pageCount?.filters) {
      setItemOffset(props?.pageCount?.filters?.page);
      setItemsPerPage(props?.pageCount?.filters?.limit);
    }
    else if (props?.pageCount?.filter) {
      setItemOffset(props?.pageCount?.filter?.page);
      setItemsPerPage(props?.pageCount?.filter?.limit);
    }
    else {
      setItemOffset(props?.pageCount?.page);
      setItemsPerPage(props?.pageCount?.limit)
    }
  }, [props?.pageCount]);

  const pageCount = Math?.ceil(props?.tableData?.total / itemsPerPage);


  const onSortingField = (data: any) => {
    const order =
      data === sortingField && sortingOrder === "asc" ? "desc" : "asc";
    setSortingField(data);
    setSortingOrder(order);
    if (data) {
      const reversed = order === "asc" ? 1 : -1;
      const preparedData = props?.tableData?.data?.sort(
        (a: any, b: any) =>
          reversed * a[data]?.toString().localeCompare(b[data])
      );
    }
  };


  const handleRowClick = (type: string, data: any) => {
    if (props.onRowClickEvent) {
      const clickData: any = { data, type };
      props.onRowClickEvent(clickData);
    }
  };

  const onAction = (data: any, type: string) => {
    if (props.onActions) {
      const actionData: any = { data, type };
      props?.onActions(actionData);
    }
  };

  const handlePageClick = (e: any, value: any) => {
    if (props.onSelectPageChange) {
      props.onSelectPageChange(value - 1);
    }
    setItemOffset(value - 1);
  };

  // Function to handle individual row selection
  const handleRowSelect = (uuid: any) => {
    setSelectedItems((prevSelected: any) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(uuid)) {
        newSelected.delete(uuid); // Deselect if already selected
      } else {
        newSelected.add(uuid); // Select if not already selected
      }
      if (props.selectCheckBox) {
        props.selectCheckBox(newSelected);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (event: any) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      const allUuids = new Set(
        items.map((item) => item.uuid || item.product_uuid)
      );
      setSelectedItems(allUuids);
      if (props.selectCheckBox) {
        props.selectCheckBox(allUuids);
      }
    } else {
      // Clear selectedItems
      setSelectedItems(new Set());
    }
  };

  const handleToggleStatus = (data: any) => {
    const updatedStatus = data.status === 1 ? 0 : 1;
    const updatedData: any = { ...data, status: updatedStatus };

    // Call the onActions callback to handle the updated status if needed
    if (props.onActions) {
      props.onActions({ data: updatedData, type: "toggle" });
    }
  };

  return (
    <div>
      <div className="mt-2">
        <div className="row">
          <div className="col-12">
            <div className="main-content">
              <div className="mt-1">
                <div className="row">
                  <div className="col">
                    <div className="card table-card">
                      <div className="table-responsive">
                        <table className="table align-items-center table-flush">
                          <thead className="thead-light">
                            <tr>
                              {props.canShowCheckbox && (
                                <th>
                                  <label className="custom-checkbox">
                                    <input
                                      type="checkbox"
                                      onChange={handleSelectAll} // Implement the select all logic as needed
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                </th>
                              )}
                              {props.TableCols.map((el, index) => (
                                <React.Fragment key={index}>
                                  {el.canShowColumn && (
                                    <th
                                      onClick={() =>
                                        el.sortable
                                          ? onSortingField(el.control)
                                          : null
                                      }
                                    >
                                      {<span>{el.title}</span>}
                                      <span>
                                        {canShowDefaultArrow &&
                                          index === 0 &&
                                          el.sortable &&
                                          sortingField !== el.control && (
                                            <i className="bi bi-arrow-down cursor-pointer p-2 "></i>
                                          )}
                                        {sortingField &&
                                          sortingField === el.control && (
                                            <i
                                              className={`${sortingOrder === "asc"
                                                  ? "bi bi-arrow-down ms-1 fw-bold cursor-pointer "
                                                  : "bi bi-arrow-up ms-1 fw-bold cursor-pointer"
                                                }`}
                                            ></i>
                                          )}
                                      </span>
                                    </th>
                                  )}
                                </React.Fragment>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((data: any, i: number) => (
                              <tr key={i}>
                                {props.canShowCheckbox && (
                                  <td>
                                    <label className="custom-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={selectedItems.has(
                                          data.uuid || data?.product_uuid
                                        )}
                                        onChange={() =>
                                          handleRowSelect(
                                            data.uuid || data?.product_uuid
                                          )
                                        }
                                      />
                                      <span className="checkmark"></span>
                                    </label>
                                  </td>
                                )}
                                {props.TableCols.map(
                                  (el: any, index: number) => (
                                    <React.Fragment key={index}>
                                      {el.canShowColumn && (
                                        <td>
                                          {el.control !== "status" &&
                                            el.control !== "is_active" &&
                                            el.control !==
                                            "mobile_number_verified" &&
                                            el.control !== "wallet_status" &&
                                            el.control !== "payment_status" &&
                                            el.control !== "order_status" &&
                                            !el.isLink &&
                                            !el.isDateFormat &&
                                            data[el.control]}
                                          {el.control ===
                                            "mobile_number_verified" && (
                                              <span>
                                                {data[el.control] === 1 ? (
                                                  <span className="text-success fw-bold">
                                                    Completed
                                                  </span>
                                                ) : (
                                                  <span className="text-primary fw-bold">
                                                    Pending
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          {el.isLink && (
                                            <span
                                              className="primary-color cursor-pointer"
                                              onClick={() =>
                                                onAction(data, "link")
                                              }
                                            >
                                              {data[el.control]?.toString()}
                                            </span>
                                          )}
                                          {(el.control === "status" ||
                                            el.control === "is_active") && (
                                              <span>
                                                {data[el.control] === 1 ? (
                                                  <span className="text-success fw-bold">
                                                    Active
                                                  </span>
                                                ) : (
                                                  <span className="text-danger fw-bold">
                                                    InActive
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                          {el.control === "status_toggle" && (
                                            <span>
                                              <label className="switch">
                                                <input
                                                  type="checkbox"
                                                  checked={data.status === 1}
                                                  onChange={() => handleToggleStatus(data)}
                                                />
                                                <span className="slider round"></span>
                                              </label>
                                            </span>
                                          )}
                                          {el.title === "Actions" && (
                                            <div className="actions-view">
                                              <span>
                                                {el.isView && (
                                                  <span
                                                    data-toggle="tooltip"
                                                    data-placement="top"
                                                    title="view"
                                                  >
                                                    <button className="px-4 primary-bg text-white rounded border-0 py-1" onClick={() =>onAction(data, "view")}>view</button>
                                                  </span>
                                                )}
                                                {el.isEdit && (
                                                  <span
                                                    data-toggle="tooltip"
                                                    data-placement="top"
                                                    title="edit"
                                                  >
                                                    <i
                                                      className="bi bi-pencil-square cursor-pointer edit-icon p-2"
                                                      onClick={() =>
                                                        onAction(data, "edit")
                                                      }
                                                    ></i>
                                                  </span>
                                                )}
                                                {el.isDelete && (
                                                  <span
                                                    data-toggle="tooltip"
                                                    data-placement="top"
                                                    title="delete"
                                                  >
                                                    <i
                                                      className="bi bi-trash text-danger fw-bold icon-size cursor-pointer p-2"
                                                      onClick={() =>
                                                        onAction(data, "delete")
                                                      }
                                                    ></i>
                                                  </span>
                                                )}
                                              </span>
                                            </div>
                                          )}
                                          {el?.isDateFormat && (
                                            <span>
                                              {data[el.control] === "" ||
                                                data[el.control] === null
                                                ? ""
                                                : moment(
                                                  data[el.control]
                                                ).format("DD-MM-YYYY")}
                                            </span>
                                          )}
                                        </td>
                                      )}
                                    </React.Fragment>
                                  )
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {items?.length === 0 && (
                          <div className="mt-3 text-center">
                            No records found.
                          </div>
                        )}
                      </div>
                    </div>
                    {items?.length !== 0 && (
                      <div className="card-footer">
                        {props.pagination && (
                          <div className="d-flex justify-content-end mt-4">
                            <Pagination
                              defaultPage={itemOffset + 1}
                              count={pageCount}
                              page={itemOffset + 1}
                              onChange={handlePageClick}
                              variant="outlined"
                              shape="circular"
                              color="primary"
                            />
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
